import './SupportPresenceLayer.scss';
import type { CSSProperties, ReactNode } from 'react';
import clsx from 'clsx';
import { HIGHLIGHTER_OPACITY } from '@srm/screen-sharing/light';

export type SupportPresenceTone = 'view' | 'control';

export interface ISupportPresencePoint {
    x: number;
    y: number;
}
export interface ISupportPresenceMark extends ISupportPresencePoint {
    id: number;
    /** Цвет сотрудника; без него — цвет тона */
    color?: string;
}
export interface ISupportPresenceRect {
    left: number;
    top: number;
    width: number;
    height: number;
}
/** Курсор одного из сотрудников в координатах окна */
export interface ISupportPresenceCursor extends ISupportPresencePoint {
    id: string;
    name?: string;
    color?: string;
}
/** Кольцо «Смотрите сюда» сотрудника */
export interface ISupportPresenceBeacon extends ISupportPresenceMark {
    name?: string;
}
/** Пометка сотрудника, уже переведённая в координаты окна */
export type ISupportPresenceAnnotation = {
    id: string;
    color: string;
    /** Автор — у подписи */
    author?: string;
} & (
    | {
          kind: 'path';
          d: string;
          fill?: boolean;
          /** Толщина, px; без неё — обычная толщина слоя */
          width?: number;
          /** Маркер: широкая полупрозрачная линия с плоскими концами */
          highlight?: boolean;
      }
    | { kind: 'laser'; segments: { d: string; opacity: number }[] }
    | { kind: 'text'; x: number; y: number; text: string }
    /** Номер шага — кружок с цифрой */
    | { kind: 'step'; x: number; y: number; number: number }
);

export interface ISupportPresenceLayerProps {
    /** `view` — просмотр (синий), `control` — управление (янтарный) */
    tone: SupportPresenceTone;
    /**
     * Захват без Element Capture: курсор попадает в видео и возвращается сотруднику эхом. Тогда он тонкий и без
     * подписи (PLAN.md §7.1).
     */
    plain?: boolean;
    /** Курсоры сотрудников — каждый своим цветом (без цвета — цветом тона) и с именем */
    cursors?: ISupportPresenceCursor[];
    /** Рябь кликов */
    ripples?: ISupportPresenceMark[];
    /** Кольца «Смотрите сюда» сотрудников — их цветами */
    beacons?: ISupportPresenceBeacon[];
    /** Пометки сотрудников (перо, маркер, стрелка, рамка, овал, лазер, шаг, подпись) */
    annotations?: ISupportPresenceAnnotation[];
    /** Поле, в которое сейчас вводит сотрудник */
    focus?: ISupportPresenceRect | null;
    /** Курсоры, которые рисует владелец со своим состоянием (`SupportPresenceCursor`): поверх остального слоя */
    children?: ReactNode;
}

const colorStyle = (color: string | undefined): CSSProperties | undefined =>
    color
        ? // Цвета участников одинаковы в обеих темах: подписи на них — всегда белые.
          ({
              '--support-presence-person': color,
              '--support-presence-on-person': 'var(--static-white)',
          } as CSSProperties)
        : undefined;

export interface ISupportPresenceCursorProps {
    point: ISupportPresencePoint;
    name?: string;
    color?: string;
    plain?: boolean;
}

/** Курсор сотрудника в координатах окна. */
export const SupportPresenceCursor = ({ point, name, color, plain }: ISupportPresenceCursorProps) => (
    <div
        className={clsx(
            'support-presence__cursor',
            plain && 'support-presence__cursor--plain',
            color && 'support-presence__cursor--person',
        )}
        data-testid="support-presence-cursor"
        style={{ transform: `translate3d(${point.x}px, ${point.y}px, 0)`, ...colorStyle(color) }}
    >
        <svg viewBox="0 0 24 24" className="support-presence__arrow">
            <path d="M4 2.5 19.5 13l-7 1.4-3.6 6.6z" />
        </svg>
        {!plain && name && <span className="support-presence__tag">{name}</span>}
    </div>
);

/**
 * Оверлей присутствия сотрудников у пользователя: курсоры с подписями, рябь кликов, кольца «Смотрите сюда» с
 * затемнением, пометки (перо, маркер, стрелка, рамка, овал, гаснущий лазер, номер шага, подпись с именем автора)
 * цветами участников и янтарная обводка поля ввода. Слой фиксирован на всё окно и не перехватывает указатель.
 */
const SupportPresenceLayer = ({
    tone,
    plain,
    cursors = [],
    ripples = [],
    beacons = [],
    annotations = [],
    focus,
    children,
}: ISupportPresenceLayerProps) => {
    const shapes = annotations.filter((item) => item.kind === 'path' || item.kind === 'laser');
    return (
        <div
            className={clsx('support-presence', `support-presence--${tone}`)}
            aria-hidden
            data-testid="support-presence"
        >
            {shapes.length > 0 && (
                <svg className="support-presence__annotations" data-testid="support-presence-annotations">
                    {shapes.map((item) =>
                        item.kind === 'laser' ? (
                            <g key={item.id} className="support-presence__laser" style={colorStyle(item.color)}>
                                {item.segments.map((segment, index) => (
                                    <path key={index} d={segment.d} style={{ opacity: segment.opacity }} />
                                ))}
                            </g>
                        ) : item.kind === 'path' ? (
                            <path
                                key={item.id}
                                className={clsx(
                                    'support-presence__stroke',
                                    item.fill && 'support-presence__stroke--area',
                                    item.highlight && 'support-presence__stroke--highlight',
                                )}
                                data-testid="support-presence-annotation"
                                d={item.d}
                                style={{
                                    ...colorStyle(item.color),
                                    ...(item.width ? { strokeWidth: item.width } : {}),
                                    ...(item.highlight ? { strokeOpacity: HIGHLIGHTER_OPACITY } : {}),
                                }}
                            />
                        ) : null,
                    )}
                </svg>
            )}
            {annotations.map((item) =>
                item.kind === 'text' ? (
                    <div
                        key={item.id}
                        className="support-presence__note"
                        data-testid="support-presence-note"
                        style={{ left: item.x, top: item.y, ...colorStyle(item.color) }}
                    >
                        {item.author && <span className="support-presence__note-author">{item.author}</span>}
                        <span className="support-presence__note-text">{item.text}</span>
                    </div>
                ) : item.kind === 'step' ? (
                    <span
                        key={item.id}
                        className="support-presence__step"
                        data-testid="support-presence-step"
                        style={{ left: item.x, top: item.y, ...colorStyle(item.color) }}
                    >
                        {item.number}
                    </span>
                ) : null,
            )}
            {focus && (
                <div
                    className="support-presence__focus"
                    data-testid="support-presence-focus"
                    style={{ left: focus.left, top: focus.top, width: focus.width, height: focus.height }}
                />
            )}
            {ripples.map((ripple) => (
                <span
                    key={ripple.id}
                    className="support-presence__ripple"
                    data-testid="support-presence-ripple"
                    style={{ left: ripple.x, top: ripple.y, ...colorStyle(ripple.color) }}
                />
            ))}
            {beacons.map((ring) => (
                <div key={ring.id} className="support-presence__beacon" data-testid="support-presence-beacon">
                    <span
                        className="support-presence__beacon-ring"
                        style={{ left: ring.x, top: ring.y, ...colorStyle(ring.color) }}
                    >
                        <span className="support-presence__beacon-label">
                            {ring.name ? `${ring.name}: смотрите сюда` : 'Смотрите сюда'}
                        </span>
                    </span>
                </div>
            ))}
            {cursors.map((item) => (
                <SupportPresenceCursor key={item.id} point={item} name={item.name} color={item.color} plain={plain} />
            ))}
            {children}
        </div>
    );
};

export default SupportPresenceLayer;
